import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Alert,
  AppError,
  EmptyState,
  Loader,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type SortDescriptor,
} from "@bfc-software/pallet";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useIssApi } from "@bfc-software/bfc-web-ui/lib/iss-api-client";

/**
 * ISS's /customer-recs/list only offers a substring match on `name`, but the
 * design's search box matches the customer ID, city and postal code as well.
 * So the whole list (up to ISS's own max page size) is fetched once and the
 * search happens client-side, as locations.tsx does for slots.
 */
const FETCH_LIMIT = 500;

type CustomersSearch = {
  q: string;
};

type CustomerRow = {
  id: number;
  location_id: string;
  name: string;
  name_2?: string | null;
  address_1?: string | null;
  address_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
};

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/customers",
)({
  validateSearch: (search?: Record<string, unknown>): CustomersSearch => ({
    q: typeof search?.["q"] === "string" ? search["q"] : "",
  }),
  component: RouteComponent,
});

/** The fields the search box matches against. */
function searchHaystack(row: CustomerRow): string {
  return [row.location_id, row.name, row.city, row.postal_code]
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
}

function Optional({ value }: { value?: string | null }) {
  return value ? (
    <>{value}</>
  ) : (
    <span className="text-on-surface-variant">—</span>
  );
}

export function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useIssApi();
  const [sortDir, setSortDir] = useState<"ascending" | "descending">(
    "ascending",
  );

  const customersQuery = client.useQuery("get", "/customer-recs/list", {
    params: { query: { limit: FETCH_LIMIT, offset: 0 } },
  });

  const customers = (customersQuery.data?.data ?? []) as CustomerRow[];

  const trimmedQuery = search.q.trim().toLowerCase();
  const filtered = trimmedQuery
    ? customers.filter((row) => searchHaystack(row).includes(trimmedQuery))
    : customers;

  const sorted = [...filtered].sort((a, b) =>
    sortDir === "ascending"
      ? a.location_id.localeCompare(b.location_id)
      : b.location_id.localeCompare(a.location_id),
  );

  const totalOnServer = customersQuery.data?.pagination.total_count ?? 0;
  const isTruncated = totalOnServer > customers.length;

  const sortDescriptor: SortDescriptor = {
    column: "location_id",
    direction: sortDir,
  };

  const { isLoading, isError } = customersQuery;

  return (
    <div className="bg-surface h-full w-full">
      <div className="page-shell">
        <header className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">{t("customers.heading")}</h1>
            <p className="page-subtitle">
              {t("customers.subheading", { count: totalOnServer })}
            </p>
          </div>
          <div className="page-header-right">
            <div className="page-header-searches">
              <Search
                aria-label={t("customers.searchAriaLabel")}
                placeholder={t("customers.searchPlaceholder")}
                value={search.q}
                onChange={(value) =>
                  navigate({
                    search: { q: String(value) },
                    resetScroll: false,
                  })
                }
                onClear={() =>
                  navigate({ search: { q: "" }, resetScroll: false })
                }
              />
            </div>
          </div>
        </header>

        {!isLoading && !isError && isTruncated ? (
          <Alert
            severity="warning"
            title={t("customers.truncatedNotice", {
              shown: customers.length,
              total: totalOnServer,
            })}
          />
        ) : null}

        <div className="table-frame">
          {isLoading ? (
            <Loader type="page" />
          ) : isError ? (
            <AppError
              description={t("customers.failed")}
              resolution={t("customers.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("customers.table.ariaLabel")}
              isResizable={false}
              sortDescriptor={sortDescriptor}
              onSortChange={(descriptor) =>
                setSortDir(
                  descriptor.direction === "descending"
                    ? "descending"
                    : "ascending",
                )
              }
            >
              <TableHead>
                <TableHeaderCell
                  id="location_id"
                  isRowHeader={true}
                  isSortable={true}
                >
                  {t("customers.table.customerId")}
                </TableHeaderCell>
                <TableHeaderCell>{t("customers.table.name")}</TableHeaderCell>
                <TableHeaderCell>{t("customers.table.name2")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("customers.table.address1")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("customers.table.address2")}
                </TableHeaderCell>
                <TableHeaderCell>{t("customers.table.city")}</TableHeaderCell>
                <TableHeaderCell>{t("customers.table.state")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("customers.table.postalCode")}
                </TableHeaderCell>
              </TableHead>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <EmptyState heading={t("customers.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  sorted.map((row) => (
                    <TableRow key={row.id} data-test-role="customer-row">
                      <TableCell>
                        <span className="font-mono font-strong">
                          {row.location_id}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-strong">{row.name}</span>
                      </TableCell>
                      <TableCell>
                        <Optional value={row.name_2} />
                      </TableCell>
                      <TableCell>
                        <Optional value={row.address_1} />
                      </TableCell>
                      <TableCell>
                        <Optional value={row.address_2} />
                      </TableCell>
                      <TableCell>
                        <Optional value={row.city} />
                      </TableCell>
                      <TableCell>
                        <Optional value={row.state} />
                      </TableCell>
                      <TableCell>
                        <span className="font-mono">
                          <Optional value={row.postal_code} />
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
